import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'

import { useBowtieLanguage, useBowtieStateds } from '../../../../../../../../../hooks'
import { IReduxActions, IReduxReducers } from '../../../../../../../../../store'
import { IBarrierTypeIcon, IEscalationFactor, IZoomLevels } from '../../../../../../../../../types'
import { SpecialStatus } from '../../../../../../../../../types/enums'
import { Icon } from '../../../../../../../../../utils'
import {
    barrierWidth,
    unfocusOpacity,
    escalationWidth,
    escalationHeight,
    barrierHeight,
    BARRIER_GAP_X
} from '../../../../../../../../../utils/constants'
import { EscalationFactorView } from '../../../../../../../../BowtieViews'
import { Barrier } from '../../Barrier'
import { TextContainer } from './styles'

interface IProps {
    x: number
    y: number
    type: 'threat' | 'consequence'
    escalationData: IEscalationFactor
    /** NOTE: data id is `esc-${barrierData.id}` */
    dataId: string
    event: EscalationFactorView
    uid: string
    xFather: number
    yFather: number
    rootXIndex: number
    zoomLevel: IZoomLevels
    barrierTypeIconMap: Map<number, IBarrierTypeIcon>
}

export const EscalationFactor: React.FC<IProps> = ({
    uid,
    escalationData,
    event: view,
    type,
    x,
    y,
    xFather,
    yFather,
    rootXIndex,
    zoomLevel,
    barrierTypeIconMap
}) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const i18n = useBowtieLanguage()
    const barrierEvents = useBowtieStateds(view.barriersView)

    const {
        consequencesExpandedBarriers,
        threatsExpandedBarriers,
        highlightDegradedBarriers,
        searchDataId
    } = useSelector((state: IReduxReducers) => ({
        ...state.bowtieExpandedBarriers,
        ...state.highlightDegradedBarriers,
        ...state.searchBarrier
    }))
    const [inactiveBarriersLength, setInactiveBarriersLength] = useState(0)
    const [stateExpanded, setStateExpanded] = useState(false)

    useEffect(() => {
        if (barrierEvents) {
            const inactiveBarriers = []

            barrierEvents.forEach(event => {
                if (!event || event?.status === SpecialStatus.INACTIVE) {
                    inactiveBarriers.push(event)
                }
            })

            setInactiveBarriersLength(inactiveBarriers.length)
        }
    }, [barrierEvents])

    useEffect(() => {
        const prevEscIdArr = uid.split('|')
        prevEscIdArr.pop()
        const prevEscId = prevEscIdArr.join('|')
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
        if (expandedBarriers.length > 0) {
            dispatch<IReduxActions['bowtieExpandedBarriers']>({
                type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                payload: {
                    newExpandedBarriers: expandedBarriers.map(e => {
                        if (e.barrierId === prevEscId) {
                            e.escalations = e.escalations.map(es => {
                                if (es.escId === uid) {
                                    es.y = y
                                }

                                return es
                            })
                        }

                        return e
                    })
                }
            })
        }
    }, [y]) // eslint-disable-line react-hooks/exhaustive-deps

    const toggleBarriers = (): void => {
        const prevEscIdArr = uid.split('|')
        prevEscIdArr.pop()
        const prevEscId = prevEscIdArr.join('|')
        const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers

        if (expandedBarriers.length > 0) {
            if (stateExpanded) {
                dispatch<IReduxActions['bowtieExpandedBarriers']>({
                    type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                    payload: {
                        newExpandedBarriers: expandedBarriers.map(e => {
                            if (e.barrierId === prevEscId) {
                                e.escalations = e.escalations.map(es => {
                                    if (es.escId === uid) {
                                        es.x = x
                                    }

                                    return es
                                })
                            }

                            return e
                        })
                    }
                })
                dispatch<IReduxActions['bowtieExpandedBarriers']>({
                    type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                    payload: { newExpandedBarriers: expandedBarriers.filter(e => !e.barrierId.startsWith(uid)) }
                })
                setStateExpanded(false)
            } else {
                dispatch<IReduxActions['bowtieExpandedBarriers']>({
                    type: type === 'threat' ? 'SET_THREAT_EXPANDED_BARRIERS' : 'SET_CONSEQUENCE_EXPANDED_BARRIERS',
                    payload: {
                        newExpandedBarriers: expandedBarriers.map(e => {
                            const newX =
                                type === 'threat'
                                    ? x - (escalationData?.barriers ?? []).length * (barrierWidth + BARRIER_GAP_X)
                                    : x + (escalationData?.barriers ?? []).length * (barrierWidth + BARRIER_GAP_X)
                            if (e.barrierId === prevEscId) {
                                e.escalations = e.escalations.map(es => {
                                    if (es.escId === uid) {
                                        es.x = newX
                                    }

                                    return es
                                })
                            }

                            return e
                        })
                    }
                })

                setStateExpanded(true)
            }
        }
    }

    useEffect(() => {
        if (searchDataId?.startsWith(`${uid}|`)) {
            if (!stateExpanded) {
                setTimeout(() => toggleBarriers(), 100)
            }
        } else if (searchDataId === uid) {
            dispatch<IReduxActions['searchBarrier']>({
                type: 'SET_SEARCH_BARRIER_POSITION',
                payload: {
                    newSearchPosition: {
                        x:
                            type === 'threat'
                                ? stateExpanded
                                    ? x +
                                      escalationWidth / 2 -
                                      (escalationData.barriers ?? []).length * (barrierWidth + BARRIER_GAP_X)
                                    : x + escalationWidth / 2
                                : stateExpanded
                                ? x -
                                  escalationWidth / 2 +
                                  (escalationData.barriers ?? []).length * (barrierWidth + BARRIER_GAP_X)
                                : x - escalationWidth / 2,
                        y: y + escalationHeight / 2
                    }
                }
            })
        }
    }, [searchDataId]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <path
                d={`M ${xFather + barrierWidth / 2} ${(zoomLevel !== 'level2' ? 14 : barrierHeight) + yFather} V ${y +
                    escalationHeight / 2}  H ${
                    type === 'threat'
                        ? stateExpanded
                            ? x +
                              escalationWidth -
                              (escalationData.barriers ?? []).length * (barrierWidth + BARRIER_GAP_X)
                            : x + escalationWidth
                        : stateExpanded
                        ? x - escalationWidth + (escalationData.barriers ?? []).length * (barrierWidth + BARRIER_GAP_X)
                        : x - escalationWidth
                }`}
                className={`bowtie-escalation-line ${inactiveBarriersLength ? 'warn' : ''}`}
                fill="none"
                strokeWidth={4}
                opacity={highlightDegradedBarriers ? (inactiveBarriersLength ? 1 : unfocusOpacity) : 1}
                stroke={inactiveBarriersLength ? theme['yellow-700'] : theme['gray-400']}
            />
            <g
                opacity={highlightDegradedBarriers ? (inactiveBarriersLength ? 1 : unfocusOpacity) : 1}
                transform={`translate(${
                    stateExpanded
                        ? type === 'threat'
                            ? x - (escalationData.barriers ?? []).length * (barrierWidth + BARRIER_GAP_X)
                            : x + (escalationData.barriers ?? []).length * (barrierWidth + BARRIER_GAP_X)
                        : x
                }, ${y})`}
                id={uid}
            >
                <g transform={`${type === 'consequence' ? `rotate(180 0 0), translate(0 ${-escalationHeight})` : ''}`}>
                    <path
                        d="M124.307 1H5.06542C2.82015 1 1 2.82015 1 5.06542V64.9346C1 67.1798 2.82015 69 5.06542 69H124.249C125.911 69 127.405 67.9887 128.023 66.4462L140.377 35.6018C140.776 34.6057 140.765 33.4923 140.347 32.5043L128.05 3.47957C127.414 1.97645 125.94 1 124.307 1Z"
                        fill="#F9E47F"
                        stroke="#3866B1"
                        strokeWidth="0.813084"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </g>
                <foreignObject
                    x={type === 'consequence' ? -escalationWidth + 17 : 8}
                    y={8}
                    width={escalationWidth - 25}
                    height={escalationHeight - 16}
                    style={{ overflow: 'hidden' }}
                >
                    <TextContainer>{escalationData?.name ?? i18n('Escalation Factor')}</TextContainer>
                </foreignObject>
                <Icon
                    type="arrow"
                    x={
                        type === 'threat'
                            ? stateExpanded
                                ? escalationWidth + 10
                                : escalationWidth + 30
                            : stateExpanded
                            ? -(escalationWidth + 10)
                            : -(escalationWidth + 30)
                    }
                    y={escalationHeight / 2 - 8}
                    scale={type === 'threat' ? (stateExpanded ? '1' : '1 -1') : stateExpanded ? '1 -1' : '1'}
                    rotate={-90}
                    color={theme['gray-800']}
                    auxliarColor={theme['gray-200']}
                    clickEvent={toggleBarriers}
                />
            </g>
            {stateExpanded &&
                view.barriersView?.map((barrierView, index, arr) => {
                    const barrier = barrierView.barrier
                    const barrierX =
                        type === 'threat'
                            ? x + barrierWidth / 1.25 - (barrierWidth + BARRIER_GAP_X) * (arr.length - 1 - index)
                            : x - barrierWidth / 1.25 + (barrierWidth + BARRIER_GAP_X) * (arr.length - 1 - index)

                    const barrierY = y + escalationHeight / 2 + 8
                    const expandedBarriers = type === 'threat' ? threatsExpandedBarriers : consequencesExpandedBarriers
                    const expanded = []
                    for (let i = 0; i < index; i++) {
                        expanded.push(
                            expandedBarriers
                                .filter(el => el.barrierId.startsWith(`${uid}|bar-${arr[i].barrier.id}`))
                                .map(d => (d.escalations ?? []).length)
                                .reduce((a, b) => a + b, 0)
                        )
                    }
                    const brothersLength = expanded.reduce((a, b) => a + b, 0)

                    return (
                        <Barrier
                            key={`${uid}|bar-${barrier.id}`}
                            x={barrierX}
                            y={barrierY}
                            barrierData={barrier}
                            type={type}
                            event={barrierView}
                            uid={`${uid}|bar-${barrier.id}`}
                            dataId={`bar-${barrier.id}`}
                            rootXIndex={rootXIndex}
                            barrierBrothersY={brothersLength}
                            zoomLevel={zoomLevel}
                            barrierTypeIconMap={barrierTypeIconMap}
                        />
                    )
                })}
        </>
    )
}
